import React, { FC, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Edit, Undo } from '@mui/icons-material';
import { FormControlLabel, InputAdornment, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { hasWildfirePermissionsSelector } from 'helpers/auth0';
import { getRtkErrorMessage } from 'helpers/getErrorMessage';
import { USER_SPLIT_ERROR, isValidUserSplitPercent, toUserSplitFraction, toUserSplitPercent } from 'helpers/ssr';
import { useUpdateApplicationSsrMutation } from 'reduxState/store/applicationManagement/api';
import { ApplicationManagement } from '../../../reduxState/store/applicationManagement/types';
import { ConfirmEdit } from './ConfirmEdit';
import '../scss/AppSsr.scss';

export interface AppSsrProps {
  applicationId: string;
  applicationManagement: ApplicationManagement;
}

export const AppSsr: FC<React.PropsWithChildren<AppSsrProps>> = ({ applicationId, applicationManagement }) => {
  const { user } = useAuth0();
  const [isDisabled, setDisabled] = useState(true);
  const [ssrEnabled, setSsrEnabled] = useState(applicationManagement.EnableSSR ?? false);
  const [splitPercent, setSplitPercent] = useState(toUserSplitPercent(applicationManagement.UserSplit));
  const [updateApplicationSsr, { isLoading }] = useUpdateApplicationSsrMutation();

  // Depend on primitives so a parent re-render creating a new object reference
  // does not clobber in-progress edits.
  useEffect(() => {
    setSsrEnabled(applicationManagement.EnableSSR ?? false);
    setSplitPercent(toUserSplitPercent(applicationManagement.UserSplit));
  }, [applicationManagement.EnableSSR, applicationManagement.UserSplit]);

  // Defense in depth: the parent already gates this section behind hasWildfirePermissionsSelector,
  // but gate here too so the component is safe if reused elsewhere. Placed after all hooks to
  // satisfy the Rules of Hooks. (Server-side, deviceapi #5664 only applies EnableSSR/UserSplit
  // for Wildfire callers, so this is a UX guard, not the authorization boundary.)
  if (!hasWildfirePermissionsSelector(user)) return null;

  const isPercentValid = !ssrEnabled || isValidUserSplitPercent(splitPercent);

  const cancelEdit = () => {
    if (isLoading) return;
    setSsrEnabled(applicationManagement.EnableSSR ?? false);
    setSplitPercent(toUserSplitPercent(applicationManagement.UserSplit));
    setDisabled(true);
  };

  const handleSave = async () => {
    // SSR is saved through its own endpoint, so this sends only the SSR fields. The split is sent only when
    // enabling; on disable the stored split is kept, so re-enabling restores it.
    const userSplit = ssrEnabled ? toUserSplitFraction(splitPercent) : null;
    try {
      await updateApplicationSsr({
        appId: applicationId,
        enableSsr: ssrEnabled,
        ...(userSplit !== null ? { userSplit } : {}),
        author: user?.email ?? 'missing email',
      }).unwrap();
      toast.success('SSR settings updated successfully');
      setDisabled(true);
    } catch (error: unknown) {
      const backendMessage = getRtkErrorMessage(error, '');
      if ((error as { status?: unknown } | null)?.status === 401) {
        toast.error('Failed to update SSR settings: unauthorized.');
      } else if (backendMessage) {
        toast.error(`Failed to update SSR settings: ${backendMessage}`);
      } else {
        toast.error('Failed to update SSR settings. Please try again.');
      }
      // Keep edit mode open so the operator can retry or cancel.
    }
  };

  const before = applicationManagement.EnableSSR
    ? `enabled @ ${toUserSplitPercent(applicationManagement.UserSplit) || '—'}%`
    : 'disabled';
  const after = ssrEnabled ? `enabled @ ${splitPercent}%` : 'disabled';
  const changeText = `SSR: ${before} → ${after}`;

  return (
    <div className="app-ssr settings-form-group">
      <TextField
        id="appSsrSettings"
        className={`app-ssr-field ${!isDisabled ? 'user-editing' : ''}`}
        variant="outlined"
        label="Server-Side Rendering (SSR)"
        multiline
        disabled={isDisabled || isLoading}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              {!isDisabled ? (
                <Tooltip title="Cancel editing">
                  <Undo onClick={cancelEdit} className="edit-icon" data-testid="ssr-undo-icon" />
                </Tooltip>
              ) : (
                <Edit onClick={() => setDisabled(false)} className="edit-icon" data-testid="ssr-edit-icon" />
              )}
            </InputAdornment>
          ),
          inputComponent: SsrControls as any,
          inputProps: {
            ssrEnabled,
            setSsrEnabled,
            splitPercent,
            setSplitPercent,
            isDisabled: isDisabled || isLoading,
            isPercentValid,
          },
        }}
      />
      {!isDisabled && !isLoading &&
        (isPercentValid ? (
          <ConfirmEdit
            headerText="Edit SSR Settings"
            confirmText="Confirm save to update SSR settings:"
            changeText={changeText}
            onSave={handleSave}
          />
        ) : (
          <Typography variant="body2" color="error" className="ssr-validation-error">
            A whole-number split from 1 to 100 is required when SSR is enabled
          </Typography>
        ))}
    </div>
  );
};

interface SsrControlsProps {
  ssrEnabled: boolean;
  setSsrEnabled: (v: boolean) => void;
  splitPercent: string;
  setSplitPercent: (v: string) => void;
  isDisabled: boolean;
  isPercentValid: boolean;
}

const SsrControls = React.forwardRef<HTMLDivElement, SsrControlsProps>((props, ref) => (
  <div className="ssr-controls" ref={ref}>
    <FormControlLabel
      control={
        <Switch
          checked={props.ssrEnabled}
          onChange={e => props.setSsrEnabled(e.target.checked)}
          disabled={props.isDisabled}
          inputProps={{ 'data-testid': 'ssr-toggle' } as React.InputHTMLAttributes<HTMLInputElement>}
        />
      }
      label="SSR Enabled"
      className="ssr-toggle-label"
    />
    {props.ssrEnabled && (
      <TextField
        label="User Split %"
        type="number"
        value={props.splitPercent}
        onChange={e => props.setSplitPercent(e.target.value)}
        disabled={props.isDisabled}
        inputProps={{ min: 1, max: 100, step: 1 }}
        error={!props.isPercentValid}
        helperText={!props.isPercentValid ? USER_SPLIT_ERROR : ''}
        size="small"
        className="ssr-split-input"
        data-testid="ssr-split-input"
      />
    )}
  </div>
));
